import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Joyride, ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { useAuth } from '../../context/AuthContext';
import { WORKER_TOUR_STEPS, MANAGER_TOUR_STEPS } from './tourSteps';

const STORAGE_KEY = 'sc365.replay_walkthrough';
const DEBUG = false;

/**
 * In-app guided walkthrough — controlled-mode (react-joyride v3).
 *
 * Behavior:
 *  - run stays true throughout (no toggling — that was the freeze cause).
 *  - Each step has a `before` hook that navigates to the right route and
 *    polls the DOM until the target exists. Joyride waits for it before
 *    showing the next tooltip, so there's no freeze.
 *  - On STEP_AFTER + NEXT/PREV → just bump stepIndex.
 *  - TARGET_NOT_FOUND → advance to next step (or finish if last).
 *  - skipBeacon=true so tooltip auto-opens, buttons=['back','skip','primary']
 *    so Skip is rendered (showSkipButton is gone in v3).
 *  - closeButtonAction: 'skip' so the X button also ends the tour.
 */
export default function Walkthrough() {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navigateRef = useRef(navigate);
  const locationRef = useRef(location);
  navigateRef.current = navigate;
  locationRef.current = location;

  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const completedRef = useRef(false);
  const transitioningRef = useRef(false);

  const baseSteps = useMemo(() => {
    if (user?.role === 'manager') return MANAGER_TOUR_STEPS;
    if (user?.role === 'worker') return WORKER_TOUR_STEPS;
    return [];
  }, [user?.role]);

  // Wait helper — polls DOM until selector exists AND is measurable (has size).
  // We need both because after a route change React may render the element on
  // one frame and only give it layout on the next.
  const waitForSelector = useCallback((selector, timeout = 2500) => {
    return new Promise((resolve) => {
      const check = () => {
        const el = document.querySelector(selector);
        if (el) {
          const rect = el.getBoundingClientRect();
          // width/height > 0 means CSS has committed the layout
          if (rect.width > 0 && rect.height > 0) return el;
        }
        return null;
      };
      const first = check();
      if (first) return resolve(first);
      const t0 = performance.now();
      const tick = () => {
        const found = check();
        if (found) return resolve(found);
        if (performance.now() - t0 > timeout) return resolve(null);
        requestAnimationFrame(tick);
      };
      tick();
    });
  }, []);

  // Smooth-scroll the target into view WITHOUT hiding it under fixed headers
  // or the bottom mobile nav. On mobile there's a fixed bottom nav (h~64px)
  // and often a sticky top header (h~56px), so we centre-align the target
  // in the safe zone in the middle.
  const scrollTargetIntoView = useCallback(async (el) => {
    if (!el) return;
    try {
      // Give the DOM one paint so any route-change transitions settle first.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      // Wait for the smooth-scroll animation to (roughly) finish so Joyride's
      // spotlight math uses the final rect.
      await new Promise((r) => setTimeout(r, 350));
    } catch {
      // Legacy browsers: fall back to no-op — Joyride will still position.
    }
  }, []);

  // Joyride steps with `before` hooks for route navigation + DOM wait.
  const steps = useMemo(
    () =>
      baseSteps.map((s) => ({
        target: s.target,
        placement: s.placement,
        spotlightPadding: s.spotlightPadding,
        content: (
          <div>
            <p className="text-sm font-bold mb-1 text-slate-900">{s.title}</p>
            <p className="text-sm text-slate-600 leading-relaxed">{s.content}</p>
          </div>
        ),
        before: async () => {
          // 1. Route to the correct page for this step (if not already there).
          if (s.route && locationRef.current.pathname !== s.route) {
            if (DEBUG) console.log('[walkthrough] navigating to', s.route);
            navigateRef.current(s.route);
          }
          // 2. Wait for the target to be present AND measurable in the DOM.
          const el = await waitForSelector(s.target, 2500);
          // 3. Smooth-scroll it into a comfortable position so the spotlight
          //    lands on the right element (fixes the "highlights the wrong
          //    area" bug on mobile where target was behind the bottom nav).
          if (el) await scrollTargetIntoView(el);
        },
      })),
    [baseSteps, waitForSelector, scrollTargetIntoView]
  );

  const markComplete = useCallback(async () => {
    if (completedRef.current) return;
    completedRef.current = true;
    setRun(false);
    setStepIndex(0);
    try {
      await api.post('/users/me/walkthrough-complete');
      if (refreshUser) refreshUser();
    } catch {
      // best-effort
    }
  }, [api, refreshUser]);

  // Auto-start logic — kicks off the tour at most once per mount.
  useEffect(() => {
    if (!user || !user.role) return;
    if (baseSteps.length === 0) return;
    if (completedRef.current) return;

    const replay =
      typeof window !== 'undefined' && window.localStorage.getItem(STORAGE_KEY) === '1';
    const shouldStart =
      replay || (!user.walkthroughCompleted && user.onboardingCompleted);

    if (!shouldStart) return;

    let cancelled = false;
    const t = setTimeout(async () => {
      if (cancelled) return;
      if (replay && typeof window !== 'undefined') {
        window.localStorage.removeItem(STORAGE_KEY);
      }
      // Run step 0's before-hook so the tour never opens on the wrong page.
      // The steps memo isn't in this effect's deps to avoid loops, but we
      // read via `steps` which is stable across renders that share deps.
      const firstStep = steps[0];
      if (firstStep?.before) await firstStep.before();
      else await waitForSelector(baseSteps[0].target, 2500);
      if (DEBUG) console.log('[walkthrough] starting tour at step 0');
      setStepIndex(0);
      setRun(true);
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role, user?.walkthroughCompleted, user?.onboardingCompleted, baseSteps.length]);

  const handleCallback = useCallback(
    // v3 onEvent handler: signature is (data, controls)
    (data /*, controls */) => {
      const { action, index, type, status } = data;
      if (DEBUG) {
        console.log('[walkthrough]', { type, action, index, status });
      }

      // Tour finished / skipped naturally
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        markComplete();
        return;
      }
      // Explicit close / skip button
      if (action === ACTIONS.CLOSE || action === 'skip') {
        markComplete();
        return;
      }

      // Target missing at runtime → try to remount via the target step's
      // `before` hook once, then advance if it still isn't there.
      if (type === EVENTS.TARGET_NOT_FOUND) {
        (async () => {
          const currentStep = steps[index];
          if (currentStep?.before) {
            await currentStep.before();
          }
          if (document.querySelector(steps[index]?.target)) {
            // Force Joyride to re-anchor by resetting the stepIndex to itself.
            setStepIndex(-1);
            requestAnimationFrame(() => setStepIndex(index));
            return;
          }
          const nextIdx = index + 1;
          if (nextIdx >= steps.length) {
            markComplete();
          } else {
            setStepIndex(nextIdx);
          }
        })();
        return;
      }

      // After Next / Back: bump stepIndex.
      // CRITICAL: Joyride v3 does NOT call our `before` hook on step change,
      // so we must invoke it manually here — it's what navigates the router
      // to the next step's route AND scrolls the target into view. Without
      // this, the tour points at whatever anchor is present on the current
      // page (which is why it looked misaligned or jumped).
      if (type === EVENTS.STEP_AFTER) {
        if (transitioningRef.current) return;
        transitioningRef.current = true;
        (async () => {
          try {
            let targetIndex = index;
            if (action === ACTIONS.NEXT) targetIndex = index + 1;
            else if (action === ACTIONS.PREV) targetIndex = Math.max(0, index - 1);

            if (action === ACTIONS.NEXT && targetIndex >= steps.length) {
              markComplete();
              return;
            }

            // Run the next step's `before` hook: navigates + waits + scrolls.
            const upcoming = steps[targetIndex];
            if (upcoming?.before) {
              await upcoming.before();
            }
            setStepIndex(targetIndex);
          } finally {
            transitioningRef.current = false;
          }
        })();
      }
    },
    [steps, markComplete]
  );

  if (steps.length === 0) return null;

  return (
    <Joyride
      run={run}
      steps={steps}
      stepIndex={stepIndex}
      continuous
      onEvent={handleCallback}
      options={{
        buttons: ['back', 'skip', 'primary'],
        closeButtonAction: 'skip',
        skipBeacon: true, // tooltip auto-opens; no beacon click required
        targetWaitTimeout: 2500,
        showProgress: true,
        // Let Joyride handle its own scrolling as a secondary safety net —
        // our `before` hook already scrolls the target into a comfortable
        // position, so this fills any gaps from window resizes / soft keyboard.
        disableScrolling: false,
        // Offset for fixed top headers so the target isn't hidden behind them.
        scrollOffset: 96,
        // On mobile the ancestor scroll parent is <body>; the fix breaks
        // scroll math when we have a fixed bottom nav. Disable it to force
        // window-scroll math which our scrollIntoView already used.
        disableScrollParentFix: true,
        locale: {
          back: 'Back',
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          skip: 'Skip walkthrough',
        },
        primaryColor: '#DA291C',
        textColor: '#0f1b3d',
        backgroundColor: '#ffffff',
        arrowColor: '#ffffff',
        overlayColor: 'rgba(15, 27, 61, 0.55)',
        zIndex: 10000,
      }}
      styles={{
        tooltip: { borderRadius: 14, padding: 18, fontFamily: "'Public Sans', system-ui, sans-serif" },
        tooltipTitle: { fontSize: 15, fontWeight: 700, marginBottom: 4 },
        tooltipContent: { padding: 0 },
        buttonNext: { borderRadius: 999, backgroundColor: '#DA291C', padding: '8px 16px', fontWeight: 600 },
        buttonBack: { color: '#475569', marginRight: 8, fontWeight: 500 },
        buttonSkip: { color: '#94a3b8', fontWeight: 500 },
      }}
    />
  );
}

/** Trigger a replay of the walkthrough (e.g. from a Profile/Help button). */
export function replayWalkthrough(api, refreshUser) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, '1');
  }
  api?.post?.('/users/me/walkthrough-replay').catch(() => {});
  refreshUser?.();
  window.location.reload();
}
